// Shared helpers for classifying conflict rows returned by the v5
// availability/drag-check endpoints. Used by both the appointment edit
// modal and the calendar drag-conflict dialog so the same wording is
// applied everywhere a conflict surfaces.

import type { ConflictInfo } from '@/services/appointments.service';
import { getTimezoneAbbreviation, pacificToWallClock, toLocaleTimeStringInTz } from '@/lib/facilityTimezone';

// Block-schedule rule conflicts (availability / unavailability windows).
// Mirrors the conflictType strings emitted by the API SlotAnalyzer.
export const BLOCK_CONFLICT_TYPES = new Set<string>([
  'block_outside_required',
  'block_outside_optional',
  'block_inside_unavailable_required',
  'block_inside_unavailable_optional',
  'resource_outside_required',
  'resource_outside_optional',
  'resource_inside_unavailable_required',
  'resource_inside_unavailable_optional',
  'block',
]);

// Appointment-vs-appointment overlap on a shared resource. Driven by
// `appointment_types.maxConflicts` / `conflictType`; legacy parity with
// `services/calendar.php` `checkResourceForConflicts`.
export const RESOURCE_CONFLICT_TYPES = new Set<string>(['resource', 'provider']);

export interface CategorizedConflicts {
  block: ConflictInfo[];
  resource: ConflictInfo[];
  other: ConflictInfo[];
}

export function categorizeConflicts(conflicts: ConflictInfo[]): CategorizedConflicts {
  const out: CategorizedConflicts = { block: [], resource: [], other: [] };
  for (const c of conflicts) {
    const t = c.conflictType ?? '';
    if (BLOCK_CONFLICT_TYPES.has(t)) out.block.push(c);
    else if (RESOURCE_CONFLICT_TYPES.has(t)) out.resource.push(c);
    else out.other.push(c);
  }
  return out;
}

const PACIFIC_TZ = 'America/Los_Angeles';

// Conflict rows carry the raw `scheduled_appointments` datetime string, and the
// legacy `mysql_v4` connection stores those as America/Los_Angeles wall clock
// (see api `app/Casts/PacificDateTime.php` + `Scheduler/AccountTimezone.php`).
// They are naive: no `Z`, no offset. Tagging them UTC and letting the browser
// localize shifted them twice — a 12:45 PM Eastern appointment is stored
// `09:45:00`, was read as 09:45 UTC, and rendered as "05:45 AM" to an Eastern
// viewer. Reproject Pacific -> the facility/display zone instead, so the
// dialog quotes the same clock the scheduler grid shows.
function formatTime(raw: string | null | undefined, displayTz: string): string {
  if (!raw) return '';
  const trimmed = raw.trim();

  // A value carrying an explicit UTC marker or numeric offset is a real
  // instant rather than a legacy Pacific wall clock; render it directly.
  if (/(Z|[+-]\d{2}:?\d{2})$/.test(trimmed)) {
    const d = new Date(trimmed.replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return raw;
    return toLocaleTimeStringInTz(d, 'en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: displayTz,
    });
  }

  const m = trimmed.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (!m) return raw;

  const { time } = pacificToWallClock(m[1], m[2], displayTz);
  const [h, mi] = time.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(mi)) return raw;

  // Components are already reprojected into `displayTz`; this browser-local
  // Date is only a vehicle for `toLocaleTimeString`. Same pattern as
  // `formatApptDateTimeInFacilityTz`.
  return new Date(2000, 0, 1, h, mi).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Resolve the zone abbreviation against the conflict's own date so a row near
// a DST boundary reads EST vs EDT correctly.
function zoneAbbrFor(raw: string | null | undefined, displayTz: string): string {
  const m = (raw ?? '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  const ref = m
    ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12))
    : new Date();
  return getTimezoneAbbreviation(displayTz, ref);
}

/**
 * `displayTz` is the facility IANA zone the appointment is being booked into
 * (account default when no facility is selected) — never the viewer's browser
 * zone. An appointment time belongs to the clinic.
 */
export function formatResourceConflictLine(
  c: ConflictInfo,
  displayTz: string | null | undefined,
): string {
  const tz = displayTz || PACIFIC_TZ;
  const start = formatTime(c.startTime, tz);
  const end = formatTime(c.endTime, tz);
  const subject = c.patientName?.trim() || c.reason?.trim() || 'Existing appointment';
  const detail = c.reason && c.patientName && c.reason !== c.patientName ? ` — ${c.reason}` : '';
  const window = (start && end) ? ` (${start}–${end} ${zoneAbbrFor(c.startTime, tz)})` : '';
  const max = (c.severity === 'error' && typeof c.maxConflicts === 'number')
    ? ` — maximum ${c.maxConflicts} exceeded`
    : '';
  return `${subject}${detail}${window}${max}`;
}

// Build a category-aware confirm/dialog heading from a categorized payload.
export function buildConflictHeading(cat: CategorizedConflicts): string {
  const heads: string[] = [];
  if (cat.block.length > 0) heads.push('block schedule');
  if (cat.resource.length > 0) heads.push('existing appointments');
  if (cat.other.length > 0) heads.push('appointment requirements');
  return heads.length > 0
    ? `This appointment conflicts with ${heads.join(' and ')}:`
    : 'Conflicts were detected:';
}
